import type { Breakpoint } from "@mui/material";
import type { DateTime } from "luxon";
import type { CSSProperties } from "react";
import type { Department } from "../../contexts/AuthProvider";
import { isInPTO } from "../../utils/dateUtils";
import {
	type BaseNurseShift,
	type DepartmentLocation,
	type EmploymentType,
	type Nurse,
	type NurseFullRole,
	NurseFullRoleShort,
	type NurseInShift,
	NurseRole,
	Preferences,
	PtoTypes,
	type SpecificPreference,
} from "../../utils/models";
import { getShiftWorkHours } from "../../utils/utilFunctions";
import { isNursePunchExpected, isNurseSalariedForShift } from "../../utils/wageUtils";

/**
 * Build the subtext shown under a department location's group header in Auto census grouping mode.
 * Describes which dimensions (rooms / unit / floor) drive that location's census — so the number
 * shown is transparent to the user. Accepts all locations sharing the group name so that same-named
 * variants (e.g. Nurse + Aide versions of "Assignment 1") contribute to the summary together.
 */
export const getAutoGroupSubtext = (locations: DepartmentLocation[]): string | undefined => {
	if (locations.length === 0) return undefined;
	const parts: string[] = [];

	const allRooms = new Set<string>();
	for (const loc of locations) {
		for (const room of loc.rooms ?? []) allRooms.add(room.room_name);
	}
	if (allRooms.size > 0) parts.push(`${allRooms.size} ${allRooms.size === 1 ? "room" : "rooms"}`);

	const units = Array.from(new Set(locations.map((l) => l.unit).filter((u): u is string => !!u)));
	if (units.length > 0) parts.push(`Unit: ${units.join(", ")}`);

	const floors = Array.from(new Set(locations.map((l) => l.floor).filter((f): f is string => !!f)));
	if (floors.length > 0) parts.push(`Floor: ${floors.join(", ")}`);

	return parts.length > 0 ? parts.join(" · ") : "No census source configured";
};

const PTO_RELEVANT_TYPES: PtoTypes[] = [PtoTypes.Payed, PtoTypes.Makeup] as const satisfies PtoTypes[];

const isPaidPtoReason = (reason: SpecificPreference["reason"]): boolean => PTO_RELEVANT_TYPES.includes(reason as PtoTypes);

/** The time off the schedule counts as PTO, whatever day it falls on. */
export const isPaidPto = (preference: SpecificPreference): boolean => preference.type === Preferences.Avoid && isPaidPtoReason(preference.reason);

export const isRelevantPTO = (preference: SpecificPreference, date: DateTime, dayStart: string, allAvoidTypes?: boolean): boolean => {
	return (allAvoidTypes || isPaidPtoReason(preference.reason)) && isInPTO(preference, date, dayStart);
};

const SHIFT_OVERLAP_THRESHOLD = 0.3;
// Window used to associate a partial-punch (zero-duration) actual with its scheduled shift.
// The single real timestamp (punch-in if missing-out, punch-out if missing-in) must fall within
// scheduled [start - buffer, end + buffer] for a match. 4h tolerates early-clock-in / late-clock-out.
const PARTIAL_PUNCH_MATCH_BUFFER_MS = 4 * 60 * 60 * 1000;

/** Checks if the overlap between two shifts covers >= 80% of the scheduled shift's duration */
export const hasSignificantShiftOverlap = (
	scheduledShift: { startTime: DateTime; endTime: DateTime },
	actualShift: { startTime: DateTime; endTime: DateTime },
): boolean => {
	const overlapStartMs = Math.max(scheduledShift.startTime.toMillis(), actualShift.startTime.toMillis());
	const overlapEndMs = Math.min(scheduledShift.endTime.toMillis(), actualShift.endTime.toMillis());
	const overlapMs = Math.max(0, overlapEndMs - overlapStartMs);
	const scheduledDurationMs = scheduledShift.endTime.toMillis() - scheduledShift.startTime.toMillis();
	return scheduledDurationMs > 0 && overlapMs / scheduledDurationMs >= SHIFT_OVERLAP_THRESHOLD;
};

/**
 * Decide whether an actual shift corresponds to the same real shift as a scheduled shift.
 *
 * - Full punch (both `missingPunchIn` and `missingPunchOut` falsy): delegates to
 *   `hasSignificantShiftOverlap` so existing behavior is preserved.
 * - Partial punch (exactly one of `missingPunchIn` / `missingPunchOut` true): the actual carries
 *   only one real timestamp (start for missing-out, end for missing-in) and is otherwise
 *   zero-duration. We consider it a match iff that timestamp falls inside
 *   `[scheduled.start - 4h, scheduled.end + 4h]` (covers early clock-in / late clock-out).
 * - Both punches missing (rare/defensive): returns `false`.
 */
export const actualMatchesScheduled = (
	scheduledShift: { startTime: DateTime; endTime: DateTime },
	actualShift: { startTime: DateTime; endTime: DateTime; missingPunchIn?: boolean; missingPunchOut?: boolean },
): boolean => {
	const { missingPunchIn, missingPunchOut } = actualShift;
	if (!missingPunchIn && !missingPunchOut) {
		return hasSignificantShiftOverlap(scheduledShift, actualShift);
	}
	if (missingPunchIn && missingPunchOut) return false;

	const realTimestamp = missingPunchOut ? actualShift.startTime : actualShift.endTime;
	const tsMs = realTimestamp.toMillis();
	const windowStartMs = scheduledShift.startTime.toMillis() - PARTIAL_PUNCH_MATCH_BUFFER_MS;
	const windowEndMs = scheduledShift.endTime.toMillis() + PARTIAL_PUNCH_MATCH_BUFFER_MS;
	return tsMs >= windowStartMs && tsMs <= windowEndMs;
};

/** One day's actual-mode shift data, resolved once so every consumer reads the same rows. */
export type ResolvedActualDay<T extends BaseNurseShift = NurseInShift> = {
	/** Punches we trust, plus scheduled shifts swapped in for staff who do not clock in. */
	actualShifts: T[];
	/** Just the substitutes — a schedule standing in for a punch we could not use. */
	substitutedShifts: T[];
	/** Scheduled shifts with no punch, for staff who should have clocked in. Flagged both sides. */
	missingFromScheduled: T[];
};

/**
 * Resolve what the actual-mode views should treat as "worked" for one day.
 *
 * Some staff don't have to clock in (see `isNursePunchExpected`). When one of their scheduled shifts
 * has no full matching punch, we swap the scheduled shift in and drop the unusable punch. Swapping
 * rather than adding is what keeps them out of both "No Punch" and "Missing Punch".
 *
 * Three things override the substitution, because in each case something real already explains the
 * day and inventing scheduled hours on top would be a lie:
 *   - approved PTO — they were off, so nothing stands in;
 *   - a complete punch that matches no scheduled shift — they worked something other than what was
 *     rostered, so the punch wins and the rostered shift is dropped;
 *   - a complete punch matching this shift — the punch already speaks for it.
 *
 * Substituting drops all of that nurse's half punches for the day, not just the replaced one.
 * Dropping per shift instead would leave a far-off partial behind as a second row for the nurse.
 *
 * Staff who do clock in are untouched: an unpunched shift still comes back in
 * `missingFromScheduled`, flagged on both sides, and all their punches stay in `actualShifts`.
 *
 * `cost` is dropped from the substituted shifts because a scheduled shift's cost is a projection,
 * not what the actual-mode views report.
 */
export const resolveActualDayShifts = <T extends BaseNurseShift>({
	scheduledShifts,
	actualShifts,
	getNurseById,
	ptoNurseIds,
}: {
	scheduledShifts: T[];
	actualShifts: T[];
	getNurseById: (id: number) => Nurse | undefined;
	ptoNurseIds: Set<number>;
}): ResolvedActualDay<T> => {
	const isIncomplete = (shift: BaseNurseShift): boolean => !!shift.missingPunchIn || !!shift.missingPunchOut;
	const completePunches = actualShifts.filter((actual) => !isIncomplete(actual));

	const nurseIdsWithUnrosteredPunch = new Set<number>(
		completePunches
			.filter((punch) => !scheduledShifts.some((scheduled) => scheduled.nurseId === punch.nurseId && actualMatchesScheduled(scheduled, punch)))
			.map((punch) => punch.nurseId),
	);

	const substitutedShifts: T[] = [];
	const missingFromScheduled: T[] = [];
	const substitutedNurseIds = new Set<number>();

	for (const scheduled of scheduledShifts) {
		const hasCompletePunch = completePunches.some((punch) => punch.nurseId === scheduled.nurseId && actualMatchesScheduled(scheduled, punch));
		if (hasCompletePunch) continue;

		if (!isNursePunchExpected(getNurseById, scheduled.nurseId, scheduled.startTime)) {
			if (ptoNurseIds.has(scheduled.nurseId) || nurseIdsWithUnrosteredPunch.has(scheduled.nurseId)) continue;
			substitutedShifts.push({ ...scheduled, cost: undefined });
			substitutedNurseIds.add(scheduled.nurseId);
			continue;
		}

		// They do clock in: a half punch still links to the shift, so it is not "no punch".
		if (actualShifts.some((actual) => actual.nurseId === scheduled.nurseId && actualMatchesScheduled(scheduled, actual))) continue;
		missingFromScheduled.push({ ...scheduled, cost: undefined, missingPunchIn: true, missingPunchOut: true });
	}

	const punchedShifts = actualShifts.filter((actual) => !(isIncomplete(actual) && substitutedNurseIds.has(actual.nurseId)));

	return { actualShifts: [...punchedShifts, ...substitutedShifts], substitutedShifts, missingFromScheduled };
};

/**
 * An actual row with nothing punched behind it: neither a punch-in nor a punch-out exists.
 *
 * That is exactly the shape `department.agency_scheduled_without_punch_days` writes when it promotes
 * an agency nurse's scheduled shift into an actual one — hours the scheduler never asked for, which
 * still land in PPD. Deliberately NOT `startTime === endTime` (a zero-length row is a *real* dangling
 * punch) and not a punch count: exactly one flag means one real timestamp was recorded, and one real
 * punch is real worked time that must be corrected in the punch data rather than deleted here.
 * The backend guards the same rule and answers a forged request with `shift.actual.remove.has.punches`.
 */
export const isPunchlessRow = (shift: Pick<BaseNurseShift, "missingPunchIn" | "missingPunchOut">): boolean => !!shift.missingPunchIn && !!shift.missingPunchOut;

/** Which table an Actual-view row's removal must target — `true` = the actual table, `false` = the schedule. */
export type ActualRowRemoval = { isActualRow: boolean };

/**
 * Classify an Actual-view row for removal, or `null` when it must not offer one.
 *
 * The Actual grid renders two kinds of row. Some are the SCHEDULE standing in for a punch — a
 * substitute for staff who don't clock in, or a scheduled shift whose punch never arrived — and their
 * `id` is the scheduled shift's, so removing one has to go to the schedule (`isActualRow: false`);
 * the backend's agency mirror cascade takes the promoted actual with it. Everything else is a real
 * actual row, removable only when nothing was punched (`isPunchlessRow`).
 *
 * `scheduledRowIds` is the caller's set of substituted + missing-from-scheduled ids, built once.
 */
export const resolveActualRowRemoval = (
	shift: Pick<BaseNurseShift, "id" | "missingPunchIn" | "missingPunchOut">,
	scheduledRowIds: ReadonlySet<string>,
): ActualRowRemoval | null => {
	if (scheduledRowIds.has(shift.id)) return { isActualRow: false };
	return isPunchlessRow(shift) ? { isActualRow: true } : null;
};

type ColumnWidth = {
	width: CSSProperties["width"] | Partial<Record<Breakpoint, CSSProperties["width"]>>;
	minWidth?: CSSProperties["minWidth"] | Partial<Record<Breakpoint, CSSProperties["minWidth"]>>;
};

export const getShiftTotalHours = (
	startTime: DateTime,
	endTime: DateTime,
	role: NurseFullRole,
	employmentType: EmploymentType,
	department: Pick<Department, "department_id" | "breakRulesConfig">,
	nurseJobIds?: string[],
	jobManagementEnabled?: boolean,
	nurseUnion?: string | null,
): number => {
	return getShiftWorkHours(
		department.department_id,
		endTime,
		startTime,
		department.breakRulesConfig || [],
		NurseRole[NurseFullRoleShort[role as keyof typeof NurseFullRoleShort] as keyof typeof NurseRole],
		employmentType,
		nurseJobIds,
		jobManagementEnabled,
		nurseUnion,
	);
};

/** "17:00 h" — paid hours as the staff table's shift column shows them. */
export const formatTotalHours = (totalHours: number): string => {
	const hours = Math.floor(totalHours);
	const minutes = Math.round((totalHours - hours) * 60);
	return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} h`;
};

export type ActualSections = {
	noPunch: BaseNurseShift[];
	notScheduled: NurseInShift[];
	missingPunch: NurseInShift[];
	shiftDelta: NurseInShift[];
	staff: NurseInShift[];
	deltaHoursByNurseId: Map<number, number>;
};

export const partitionActualSections = ({
	staff,
	invalidNurses,
	scheduledShifts,
	ptoNurseIds,
	department,
	shiftDeltaThresholdHours,
}: {
	staff: NurseInShift[];
	invalidNurses: BaseNurseShift[];
	scheduledShifts: BaseNurseShift[];
	ptoNurseIds: Set<number>;
	department: Pick<Department, "department_id" | "breakRulesConfig">;
	shiftDeltaThresholdHours: number;
}): ActualSections => {
	const scheduledNurseIds = new Set(scheduledShifts.map((s) => s.nurseId));

	const noPunch = invalidNurses.filter(
		(nurse) => !ptoNurseIds.has(nurse.nurseId) && !staff.some((actual) => actual.nurseId === nurse.nurseId && actualMatchesScheduled(nurse, actual)),
	);

	const notScheduled = staff.filter((nurse) => !scheduledNurseIds.has(nurse.nurseId));
	const notScheduledIds = new Set(notScheduled.map((n) => n.nurseId));

	const missingPunch = staff.filter(
		(nurse) =>
			!notScheduledIds.has(nurse.nurseId) &&
			scheduledNurseIds.has(nurse.nurseId) &&
			((nurse.missingPunchIn && !nurse.missingPunchOut) || (!nurse.missingPunchIn && nurse.missingPunchOut)),
	);
	const missingPunchIds = new Set(missingPunch.map((n) => n.nurseId));

	const regularStaff = staff.filter((nurse) => !notScheduledIds.has(nurse.nurseId) && !missingPunchIds.has(nurse.nurseId));

	const sumHoursByNurseId = (shifts: BaseNurseShift[]): Map<number, number> => {
		const totals = new Map<number, number>();
		for (const shift of shifts) {
			const hours = getShiftTotalHours(shift.startTime, shift.endTime, shift.role, shift.type, department);
			totals.set(shift.nurseId, (totals.get(shift.nurseId) ?? 0) + hours);
		}
		return totals;
	};
	const scheduledHoursByNurseId = sumHoursByNurseId(scheduledShifts);
	const actualHoursByNurseId = sumHoursByNurseId(regularStaff);

	const deltaHoursByNurseId = new Map<number, number>();
	const shiftDeltaNurseIds = new Set<number>();
	for (const [nurseId, actualHours] of actualHoursByNurseId) {
		const scheduledHours = scheduledHoursByNurseId.get(nurseId) ?? 0;
		const delta = actualHours - scheduledHours;
		deltaHoursByNurseId.set(nurseId, delta);
		if (delta > shiftDeltaThresholdHours) shiftDeltaNurseIds.add(nurseId);
	}
	const shiftDelta = regularStaff.filter((nurse) => shiftDeltaNurseIds.has(nurse.nurseId));
	const remainingStaff = regularStaff.filter((nurse) => !shiftDeltaNurseIds.has(nurse.nurseId));

	return { noPunch, notScheduled, missingPunch, shiftDelta, staff: remainingStaff, deltaHoursByNurseId };
};

/**
 * Build a per-nurse map of (actual hours - scheduled hours) across all their shifts on the day.
 * Negative or zero deltas are included so this is suitable for column-wide sorting.
 */
export const getDeltaHoursByNurseId = ({
	staff,
	scheduledShifts,
	department,
	jobManagementEnabled = false,
	getNurseById,
}: {
	staff: NurseInShift[];
	scheduledShifts: BaseNurseShift[];
	department: Pick<Department, "department_id" | "breakRulesConfig">;
	jobManagementEnabled?: boolean;
	getNurseById: (id: number) => Nurse | undefined;
}): Map<number, number> => {
	const totals = (shifts: BaseNurseShift[]): Map<number, number> => {
		const m = new Map<number, number>();
		for (const shift of shifts) {
			const hours = getShiftTotalHours(
				shift.startTime,
				shift.endTime,
				shift.role,
				shift.type,
				department,
				shift.jobId ? [shift.jobId] : undefined,
				jobManagementEnabled,
				getNurseById(shift.nurseId)?.union,
			);
			m.set(shift.nurseId, (m.get(shift.nurseId) ?? 0) + hours);
		}
		return m;
	};
	const scheduledByNurse = totals(scheduledShifts);
	const actualByNurse = totals(staff);
	// We need a shift date to look up the nurse's wage type. Any of their shifts that day works.
	const shiftStartByNurseId = new Map<number, DateTime>();
	for (const shift of [...staff, ...scheduledShifts]) {
		if (!shiftStartByNurseId.has(shift.nurseId)) shiftStartByNurseId.set(shift.nurseId, shift.startTime);
	}
	const result = new Map<number, number>();
	const allIds = new Set<number>([...scheduledByNurse.keys(), ...actualByNurse.keys()]);
	for (const id of allIds) {
		// Skip salaried staff — their extra hours are part of their salary, so shift delta is
		// meaningless for them. Wage type comes from the base wage for the shift date.
		if (isNurseSalariedForShift(getNurseById, id, shiftStartByNurseId.get(id))) continue;
		result.set(id, (actualByNurse.get(id) ?? 0) - (scheduledByNurse.get(id) ?? 0));
	}
	return result;
};

/**
 * Total $ cost of "extra" hours beyond what each nurse was scheduled for, only counting nurses
 * whose actual-vs-scheduled delta exceeds the department threshold (i.e. members of the Shift
 * Delta section). Per nurse: effective hourly rate (totalWage / actualHours) × deltaHours.
 */
export const getShiftDeltaCost = ({
	staff,
	scheduledShifts,
	department,
	shiftDeltaThresholdHours,
	getNurseWage,
	getNurseById,
}: {
	staff: NurseInShift[];
	scheduledShifts: BaseNurseShift[];
	department: Pick<Department, "department_id" | "breakRulesConfig">;
	shiftDeltaThresholdHours: number;
	getNurseWage: (nurse: NurseInShift) => number;
	getNurseById: (id: number) => Nurse | undefined;
}): number => {
	const scheduledNurseIds = new Set(scheduledShifts.map((s) => s.nurseId));
	// Mirror partitionActualSections: ignore not-scheduled and missing-punch nurses; only over-threshold counts.
	const eligible = staff.filter((n) => {
		if (!scheduledNurseIds.has(n.nurseId)) return false;
		// Skip salaried staff — their extra hours are part of their salary, so shift delta is
		// meaningless for them. Wage type comes from the base wage for the shift date.
		if (isNurseSalariedForShift(getNurseById, n.nurseId, n.startTime)) return false;
		const hasMissing = (n.missingPunchIn && !n.missingPunchOut) || (!n.missingPunchIn && n.missingPunchOut);
		return !hasMissing;
	});

	const sumHoursByNurseId = (shifts: BaseNurseShift[]): Map<number, number> => {
		const totals = new Map<number, number>();
		for (const shift of shifts) {
			const hours = getShiftTotalHours(
				shift.startTime,
				shift.endTime,
				shift.role,
				shift.type,
				department,
				undefined,
				undefined,
				getNurseById(shift.nurseId)?.union,
			);
			totals.set(shift.nurseId, (totals.get(shift.nurseId) ?? 0) + hours);
		}
		return totals;
	};
	const scheduledHoursByNurseId = sumHoursByNurseId(scheduledShifts);
	const actualHoursByNurseId = sumHoursByNurseId(eligible);

	const wageByNurseId = new Map<number, number>();
	for (const shift of eligible) {
		wageByNurseId.set(shift.nurseId, (wageByNurseId.get(shift.nurseId) ?? 0) + getNurseWage(shift));
	}

	let cost = 0;
	for (const [nurseId, actualHours] of actualHoursByNurseId) {
		const scheduledHours = scheduledHoursByNurseId.get(nurseId) ?? 0;
		const delta = actualHours - scheduledHours;
		if (delta > shiftDeltaThresholdHours) {
			const wage = wageByNurseId.get(nurseId) ?? 0;
			if (wage > 0 && actualHours > 0) cost += (wage / actualHours) * delta;
		}
	}
	return cost;
};

export const getStaffTableColumnWidths = (isHospitalDemo: boolean): Record<string, ColumnWidth> => ({
	role: { width: "70px" },
	job: { width: { xs: "8%", xxl: "10%" }, minWidth: { xs: "70px", xxl: "100px" } },
	name: { width: isHospitalDemo ? "13%" : "15%" },
	shift: { width: isHospitalDemo ? "22%" : "24%", minWidth: "200px" },
	location: { width: { xs: isHospitalDemo ? "8%" : "10%", xl: isHospitalDemo ? "14%" : "16%" }, minWidth: { xs: "100px", xl: "150px" } },
	patients: { width: "8%" },
	workload: { width: "8%" },
	notes: { width: isHospitalDemo ? "20%" : "22%", minWidth: "150px" },
	action: { width: "100px" },
});
